import { createContext, useContext, useEffect, useState } from "react";
import { fetchSettings, fetchCustomFonts } from "@/lib/api";

const SettingsContext = createContext({ sections: {}, labels: {}, isVisible: () => true, label: (k, d, e) => ({ deva: d, en: e }), heading: (k, d) => d, copy: (k, e, h) => ({ en: e, hi: h }), reload: () => {} });

// A route path like "/mantras" maps to the section key "mantras".
export const keyForPath = (path) => (path || "").replace(/^\//, "").split("/")[0];

const CACHE_KEY = "dharmartha_settings_v1";
const readCache = () => {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || "null"); } catch { return null; }
};

export function SettingsProvider({ children }) {
  const cached = readCache();
  const [sections, setSections] = useState(cached?.sections || {});
  const [labels, setLabels] = useState(cached?.labels || {});
  const [config, setConfig] = useState(cached || {});
  // If we have a cached snapshot, render immediately (no blank flicker on reload);
  // fresh data is fetched in the background and swapped in when ready.
  const [loaded, setLoaded] = useState(!!cached);
  const [customFonts, setCustomFonts] = useState([]);

  const reloadFonts = () => fetchCustomFonts().then((d) => setCustomFonts(d.items || [])).catch(() => {});

  const reload = () => fetchSettings().then((d) => {
    setSections(d.sections || {});
    setLabels(d.labels || {});
    setConfig(d || {});
    setLoaded(true);
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(d)); } catch { /* ignore quota */ }
  }).catch(() => setLoaded(true));
  useEffect(() => { reload(); reloadFonts(); }, []);

  // Default: everything visible unless explicitly set to false.
  const isVisible = (keyOrPath) => {
    if (keyOrPath?.startsWith?.("/")) {
      if (sections[keyOrPath] === false) return false; // full-path override (Pages Manager)
      return sections[keyForPath(keyOrPath)] !== false; // section-key override (legacy)
    }
    return sections[keyOrPath] !== false;
  };

  // Per-page overrides from the Pages Manager: { title, subtitle, explainer, body } as {en,hi}.
  const page = (path) => (config.pages || {})[path] || {};

  // Returns the admin-overridden {deva, en} label for a section, or the defaults.
  const label = (keyOrPath, defDeva, defEn) => {
    const key = keyOrPath?.startsWith?.("/") ? keyForPath(keyOrPath) : keyOrPath;
    const o = labels[key];
    return { deva: (o && (o.hi || o.deva)) || defDeva, en: (o && o.en) || defEn };
  };

  // Admin-overridable page heading (title + subtitle, per-language).
  // defaults: { title_deva, title_en, sub_deva, sub_en }
  const heading = (keyOrPath, defaults = {}) => {
    const key = keyOrPath?.startsWith?.("/") ? keyForPath(keyOrPath) : keyOrPath;
    const o = (config.page_headings || {})[key] || {};
    return {
      title_deva: o.title_deva || defaults.title_deva || "",
      title_en: o.title_en || defaults.title_en || "",
      sub_deva: o.sub_deva || defaults.sub_deva || "",
      sub_en: o.sub_en || defaults.sub_en || "",
    };
  };

  // Admin-overridable short UI/template copy (detail-page section labels etc.),
  // stored in page_headings under keys like "detail.on_this_page" as {en, hi}.
  const copy = (key, defEn = "", defHi = "") => {
    const o = (config.page_headings || {})[key] || {};
    return { en: o.en || defEn, hi: o.hi || defHi };
  };

  return (
    <SettingsContext.Provider value={{
      ...config,
      sections, labels, isVisible, label, heading, copy, page, reload, loaded,
      config,
      customFonts, reloadFonts,
      routeSlugs: config.route_slugs || {},
      ga4_id: config.ga4_id || "", gsc_code: config.gsc_code || "",
      razorpay_key_id: config.razorpay_key_id || "", donations_enabled: !!config.donations_enabled,
      donation_presets: config.donation_presets || [101, 501, 1100, 2100],
      org: config.org || {}, team: config.team || [],
      socials: config.socials || {},
      home_images: config.home_images || {},
      panchang_notes: config.panchang_notes || [],
      panchang_sections: config.panchang_sections || [],
      nav_config: config.nav_config || {},
      content_disclaimer: config.content_disclaimer || {},
      page_headings: config.page_headings || {},
      typography: config.typography || {},
      system_messages: config.system_messages || {},
      home_featured_count: config.home_featured_count || 6,
      legal_pages: config.legal_pages || {},
      hub_sections: config.hub_sections || [],
      deity_categories: config.deity_categories || [],
      temple_categories: config.temple_categories || [],
      home_spotlight: config.home_spotlight || {},
      panchang_fonts: config.panchang_fonts || {},
      panchang_tamil: config.panchang_tamil || {},
      panchang_lang_fonts: config.panchang_lang_fonts || {},
      panchang_glossary: config.panchang_glossary || {},
      home_promos: config.home_promos || [],
      devi_form_images: config.devi_form_images || {},
      mahavidya_images: config.mahavidya_images || {},
      amnaya_peethas: config.amnaya_peethas || {},
      home_audio: config.home_audio || {},
      crawl_enabled: config.crawl_enabled !== false,
    }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);
